import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.jsx';
import { clearJourneySessionState, setStoredApplicationNumber, getStoredApplicationNumber } from './common/utils';
import { decryptJourneyQueryValue, encryptJourneyQueryValue } from './common/utils/journeyEncryption';

class ReactRootElement extends HTMLElement {
  constructor() {
    super();
    this._root = null;
  }

  disconnectedCallback() {
    if (!this._root) return;
    this._root.unmount();
    this._root = null;
  }
}

const JOURNEY_ELEMENTS = [
  {
    elementName: 'retirement-plan',
    defaultJourneyType: 'nischit-pension',
  },
];

const _dcEncrypt = (_value) => {
  
}

/**
 * Build initial in-app path and whether we cleared (so App can reset store).
 * - On payment-success / payment-failure: legacy fallback when this CX is still
 *   mounted on shared payment pages. Prefer `inlic-common-payment-return` on those
 *   Liferay pages (do not mount journey CX + common CX together).
 * - Direct gateway callback onto journey page with status: keep PaymentReturn here.
 * - On journey with fromPaymentReturn + applicationNumber: hydrate/resume in this journey.
 * @returns {{ path: string | null, resetJourneyOnMount: boolean }}
 */
function getInitialPath(journeyRoutePath = '/retirement-plan') {
  if (typeof window === 'undefined' || !window.location) return { path: null, resetJourneyOnMount: true };
  const pathname = window.location.pathname || '';
  const search = window.location.search || '';
  const params = new URLSearchParams(search);
  const replaceBrowserUrlIfPossible = () => {
    if (typeof window === 'undefined') return;
    if (!window.history?.replaceState) return;
    try {
      const url = new URL(window.location.href);
      const raw =
        (url.searchParams.get('applicationNumber') || url.searchParams.get('applicationNo') || '')
          .trim();
      if (!raw) return;
      const decrypted = decryptJourneyQueryValue(raw) || raw;
      const encrypted = encryptJourneyQueryValue(decrypted) || decrypted;
      url.searchParams.set('applicationNumber', encrypted);
      url.searchParams.delete('applicationNo');
      window.history.replaceState(null, document.title || '', `${url.pathname}?${url.searchParams.toString()}`);
    } catch {
      // ignore
    }
  };
  if (pathname.includes('track-your-application')) {
    return { path: '/track-your-application', resetJourneyOnMount: false };
  }
  if (pathname.includes('payment-success')) {
    replaceBrowserUrlIfPossible();
    if (!params.has('status')) params.set('status', 'SUCCESS');
    return { path: `/payment-return?${params.toString()}`, resetJourneyOnMount: false };
  }
  if (pathname.includes('payment-failure')) {
    replaceBrowserUrlIfPossible();
    if (!params.has('status')) params.set('status', 'FAILED');
    return { path: `/payment-return?${params.toString()}`, resetJourneyOnMount: false };
  }
  // Direct gateway callback: journey page with applicationNumber + payment status.
  // Route through payment-return so we can resume from the correct stage instead of resetting to lead.
  const normalizedJourneyRoutePath = journeyRoutePath.startsWith('/')
    ? journeyRoutePath
    : `/${journeyRoutePath}`;
  const journeyPathToken = normalizedJourneyRoutePath.slice(1);
  const onJourneyPage = pathname.includes(journeyPathToken);

  if (onJourneyPage &&
      params.get('status') && params.get('applicationNumber')) {
    return { path: `/payment-return?${params.toString()}`, resetJourneyOnMount: false };
  }
  // Return from payment: always enter through /common-journey so the app can hydrate from journey
  // and decide the destination from a single backend source of truth.
  const applicationNumber = params.get('applicationNumber');
  const fromPaymentReturn = params.get('fromPaymentReturn');
  if (fromPaymentReturn === '1' && applicationNumber &&
      (onJourneyPage || pathname === '/' || pathname === '')) {
    return {
      path: `${normalizedJourneyRoutePath}?applicationNumber=${encodeURIComponent(applicationNumber)}&fromPaymentReturn=1`,
      resetJourneyOnMount: false,
    };
  }
  // Journey page: if we have applicationNumber in URL, start journey there.
  if (onJourneyPage) {
    const appNumFromUrl = params.get('applicationNumber')?.trim();
    if (appNumFromUrl) {
      const decrypted = decryptJourneyQueryValue(appNumFromUrl) || appNumFromUrl;
      setStoredApplicationNumber(decrypted);
      try {
        const encrypted = encryptJourneyQueryValue(decrypted);
        if (encrypted && encrypted !== appNumFromUrl) {
          params.set('applicationNumber', encrypted);
          return { path: `${normalizedJourneyRoutePath}?${params.toString()}`, resetJourneyOnMount: false };
        }
      } catch {
        // ignore
      }
      return { path: `${normalizedJourneyRoutePath}${search || ''}`, resetJourneyOnMount: false };
    }
    // SPA in-app navigation (Lead -> Quote -> Summary -> ...) uses an in-memory router,
    // so the real browser address bar never picks up `applicationNumber`. A hard refresh
    // therefore lands here with an empty URL even mid-journey. Fall back to the
    // sessionStorage-stored application number so we resume from the backend instead of
    // wiping progress and restarting at the lead form.
    const storedAppNumber = getStoredApplicationNumber()?.trim();
    if (storedAppNumber) {
      const encrypted = encryptJourneyQueryValue(storedAppNumber) || storedAppNumber;
      return {
        path: `${normalizedJourneyRoutePath}?applicationNumber=${encodeURIComponent(encrypted)}`,
        resetJourneyOnMount: false,
      };
    }
    // A dateOfBirth-only link (no applicationNumber yet, e.g. a resume link still
    // being verified) is still an in-progress resume attempt — App.jsx's dob-based
    // resume flow needs the existing session intact to look it up, so don't wipe it.
    const dateOfBirthFromUrl = params.get('dateOfBirth')?.trim();
    if (dateOfBirthFromUrl) {
      return { path: `${normalizedJourneyRoutePath}${search || ''}`, resetJourneyOnMount: false };
    }
    // Opening common journey without an application number or dob should start from
    // stage 0 (basic lead) as a genuinely fresh journey. We still clear session to
    // avoid stale persisted state (e.g. a previous application's data) leaking in.
    clearJourneySessionState({ clearLocalStorage: true });
    return { path: '/', resetJourneyOnMount: true };
  }
  // CX on home or other page: clear session so refresh on any stage shows start path, not last screen
  clearJourneySessionState({ clearLocalStorage: true });
  return { path: null, resetJourneyOnMount: true };
}

const createJourneyElement = ({ defaultJourneyType }) =>
  class JourneyElement extends ReactRootElement {
    connectedCallback() {
      if (!this._root) {
        const routePath = `/${this.tagName.toLowerCase()}`;
        const { path: paymentPath, resetJourneyOnMount } = getInitialPath(routePath);
        const initialStage = this.getAttribute('stage') || 'lead';
        const journeyType = this.getAttribute('journey-type') || defaultJourneyType;
        const leadFormName = this.getAttribute('lead-form-name');

        const journeyConfig = {
          journeyType,
          formNames: leadFormName ? { lead: leadFormName } : {},
          routePath,
        };

        this._root = createRoot(this);
        const app = paymentPath != null
          ? (
            <App
              initialPath={paymentPath}
              resetJourneyOnMount={resetJourneyOnMount}
              journeyConfig={journeyConfig}
            />
          )
          : (
            <App
              initialStage={initialStage}
              resetJourneyOnMount={resetJourneyOnMount}
              journeyConfig={journeyConfig}
            />
          );
        this._root.render(
          import.meta.env.DEV ? <React.StrictMode>{app}</React.StrictMode> : app
        );
      }
    }
  };

JOURNEY_ELEMENTS.forEach(({ elementName, defaultJourneyType }) => {
  if (!customElements.get(elementName)) {
    customElements.define(
      elementName,
      createJourneyElement({ defaultJourneyType }),
    );
  }
});
